import { useMemo } from 'react'

import { AnalyticsLevel } from '@/schema'

import { ViewLevel } from '../constants'
import { useDashboardStore } from '../store'

type SharedQueryConfig = {
  query: {
    staleTime: number
    enabled: boolean
  }
}

type AnalyticsParams = {
  level: AnalyticsLevel
  company_id?: string | null
  project_id?: string | null
}

type ProjectParams = {
  project_id: string
}

type UseProjectLevelSharedResult = {
  isProjectLevel: boolean
  shouldFetch: boolean
  queryOptions: SharedQueryConfig
  analyticsParams: AnalyticsParams
  projectParams: ProjectParams
}

/**
 * Shared hook for common project-level logic and parameters
 * Used by both useProjectLevel and useProjectLevelMetric
 */
export const useProjectLevelShared = (): UseProjectLevelSharedResult => {
  const { filters, viewLevel } = useDashboardStore()

  const isProjectLevel = useMemo(() => {
    return viewLevel === ViewLevel.PROJECTS
  }, [viewLevel])

  const shouldFetch = useMemo(() => {
    return isProjectLevel && !!filters.companyId && !!filters.projectId
  }, [isProjectLevel, filters.companyId, filters.projectId])

  // Shared query options
  const queryOptions = useMemo(
    () => ({
      query: {
        staleTime: 1000,
        enabled: shouldFetch,
      },
    }),
    [shouldFetch]
  )

  // Shared params for analytics with company and project level
  const analyticsParams = useMemo(
    () => ({
      level: AnalyticsLevel.project,
      company_id: filters.companyId,
      project_id: filters.projectId,
    }),
    [filters.companyId, filters.projectId]
  )

  // Shared params for project-specific queries
  const projectParams = useMemo(
    () => ({
      project_id: filters.projectId || '',
    }),
    [filters.projectId]
  )

  return {
    isProjectLevel,
    shouldFetch,
    queryOptions,
    analyticsParams,
    projectParams,
  }
}
